/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { ImageIcon } from "lucide-react"
import type { ChangeEvent, DragEvent, ReactNode } from "react"
import { useEffect, useId, useRef, useState } from "react"

import { cn } from "@/utils/cn"

import { buttonVariants } from "../primitives/button"
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "../primitives/item"

/**
 * Placeholder shown when neither a selected file nor a current icon is available.
 */
const ICON_PLACEHOLDER = <ImageIcon />

/**
 * MIME types accepted by the picker and checked when a file is chosen or dropped.
 *
 * The browser infers MIME types from file extensions, so this is only a
 * client-side convenience. The server remains responsible for validating the
 * uploaded file.
 */
const acceptedTypes = ["image/png", "image/jpeg"]

interface Props {
    /**
     * ID of the file input, used to associate it with its label.
     */
    id: string
    /**
     * Currently selected file, or `undefined` if none has been selected.
     */
    value: File | undefined
    /**
     * Called when the user selects or drops an accepted file.
     */
    onChange: (file: File) => void
    /**
     * Icon to show until a new file is selected.
     *
     * The caller is responsible for handling a missing icon, including
     * rendering a fallback if needed.
     *
     * @default ICON_PLACEHOLDER
     *
     * @example
     * current={<AppIcon appTypeId={appTypeId} />}
     */
    current?: ReactNode
    /**
     * Whether file selection and dropping are disabled, for example while
     * the form is saving.
     *
     * @default false
     */
    disabled?: boolean
}

/**
 * Lets the user select or drop an application icon and previews the file.
 *
 * The form owns the selected file. Clearing `value` removes the preview and
 * resets the native file input, which cannot be controlled like a text input.
 *
 * The native input is visually hidden, and the picker displays the file name
 * itself. Dropped files do not update the native input, so its built-in label
 * could otherwise show "No file chosen" or the name of a previously selected file.
 *
 * @example
 * <IconPicker
 *     id="application-type-icon"
 *     value={field.input}
 *     onChange={field.onChange}
 *     current={<AppIcon appTypeId={appTypeId} />}
 * />
 */
export const IconPicker = ({ current, disabled = false, id, onChange, value }: Props) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [rejected, setRejected] = useState(false)
    const [dragging, setDragging] = useState(false)

    const inputRef = useRef<HTMLInputElement>(null)
    const fileNameId = useId()
    const messageId = useId()

    /**
     * Accepts PNG and JPG files and marks other file types as rejected.
     *
     * @param file - File chosen in the dialog or dropped onto the picker.
     * @returns Whether the file type is accepted.
     */
    const handleFile = (file: File) => {
        const accepted = acceptedTypes.includes(file.type)
        setRejected(!accepted)
        if (accepted) onChange(file)
        return accepted
    }

    /**
     * Handles a file selected through the native file dialog.
     */
    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) handleFile(file)
    }

    /**
     * Highlights the drop zone while dragging a file over it.
     *
     * Prevents the browser from opening the dropped file in the tab, even when
     * the picker is disabled.
     */
    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        if (!disabled) setDragging(true)
    }

    /**
     * Removes the drop-zone highlight when the drag leaves the zone.
     *
     * Moving between elements inside the zone also fires `dragleave`, so ignore
     * events whose `relatedTarget` is still inside it. If `relatedTarget` is
     * missing, clear the highlight; the next `dragover` will restore it if needed.
     */
    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
        setDragging(false)
    }

    /**
     * Handles a file dropped onto the zone, unless the picker is disabled.
     *
     * Prevents the browser from opening the dropped file in the tab. After an
     * accepted drop, clears the native input so it doesn't show a stale filename
     * and the same file can be selected again through the dialog.
     */
    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setDragging(false)
        if (disabled) return

        const file = event.dataTransfer.files[0]
        if (!file) return

        // Once the drop replaced the input's file, empty the input, so it stops
        // naming that file, and choosing it again still fires `change`.
        if (handleFile(file) && inputRef.current) inputRef.current.value = ""
    }

    /**
     * Synchronizes the preview, rejection state, and native input with the
     * selection controlled by the form.
     */
    useEffect(() => {
        if (!value) {
            // The form cleared the selection, e.g. on discard or after upload.
            // Remove the preview and rejection, and reset the input so the same
            // file can be selected again.
            setPreviewUrl(null)
            setRejected(false)
            if (inputRef.current) inputRef.current.value = ""
            return
        }

        // Create a preview URL for the selected file. Revoke it when the file
        // changes or the component unmounts to release the associated resources.
        // Creating it in the effect ensures each URL has a cleanup, including
        // when React Strict Mode runs the effect more than once.
        const url = URL.createObjectURL(value)
        setPreviewUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [value])

    return (
        <Item
            variant="outline"
            data-dragging={dragging || undefined}
            className="data-dragging:border-ring data-dragging:bg-muted/50"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {/*
             * Show the selected file's preview, else the current icon, else the placeholder.
             * A placeholder icon gets a muted tile. Images are contained, so a non-square
             * icon isn't cropped. AppIcon's box is smaller than the tile and has a shadow,
             * so stretch it to the tile and drop the shadow.
             */}
            <ItemMedia
                variant="image"
                className="has-[svg]:bg-muted has-[svg]:text-muted-foreground **:data-[slot=app-icon]:size-full **:data-[slot=app-icon]:shadow-none [&_img]:object-contain"
            >
                {previewUrl ? <img src={previewUrl} alt="Icon preview" /> : (current ?? ICON_PLACEHOLDER)}
            </ItemMedia>
            <ItemContent className="min-w-0">
                <ItemTitle
                    id={fileNameId}
                    className={clsx("block max-w-full truncate", { "text-muted-foreground": !value })}
                >
                    {value?.name ?? "No file chosen"}
                </ItemTitle>
                {renderMessage(messageId, { dragging, rejected, selected: value !== undefined })}
            </ItemContent>
            <ItemActions>
                {/* Visually hidden but focusable; the label below opens the file dialog. */}
                <input
                    ref={inputRef}
                    id={id}
                    type="file"
                    accept={acceptedTypes.join(", ")}
                    aria-invalid={rejected}
                    // Announce the filename and upload hint or error to screen readers.
                    // The message ID remains in the description when its element is absent.
                    aria-describedby={`${fileNameId} ${messageId}`}
                    disabled={disabled}
                    onChange={handleFileChange}
                    className="peer sr-only"
                />
                {/* Reflect the input's focus, disabled, and invalid states on its label. */}
                <label
                    htmlFor={id}
                    className={cn(
                        buttonVariants({ variant: "outline", size: "sm" }),
                        "cursor-pointer peer-focus-visible:border-ring peer-focus-visible:ring-3 peer-focus-visible:ring-ring/50 peer-disabled:pointer-events-none peer-disabled:opacity-50 peer-aria-invalid:border-destructive"
                    )}
                >
                    Choose file
                </label>
            </ItemActions>
        </Item>
    )
}

interface MessageState {
    /**
     * Whether a file is being dragged over the picker.
     */
    dragging: boolean
    /**
     * Whether the most recently chosen file was rejected for its type.
     */
    rejected: boolean
    /**
     * Whether a file is selected and the upload hint can be hidden.
     */
    selected: boolean
}

/**
 * Renders the upload hint or validation message.
 *
 * Dragging takes precedence over rejection. A rejection message remains visible
 * alongside the previous preview, while a selected file needs no message.
 *
 * Rejections use `role="alert"` so screen readers announce them immediately.
 *
 * @param id - ID used to associate the message with the file input.
 * @param state - Current drag, rejection, and selection state.
 * @returns The message to display, or `null` when a file is selected.
 */
function renderMessage(id: string, { dragging, rejected, selected }: MessageState) {
    if (dragging)
        return (
            <ItemDescription id={id} className="m-0">
                Drop file here.
            </ItemDescription>
        )
    if (rejected)
        return (
            <ItemDescription id={id} role="alert" className="m-0 text-destructive">
                Please select a PNG or JPG file
            </ItemDescription>
        )
    if (selected) return null

    return (
        <ItemDescription id={id} className="m-0">
            Select icon file or drag and drop PNG / JPG file.
        </ItemDescription>
    )
}
